import { Button } from '@folio/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@folio/components/ui/popover';
import { SearchableSelect } from '@folio/components/ui/searchable-select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@folio/components/ui/tooltip';
import { useToast } from '@folio/components/ui/use-toast';
import { cn, formatDate } from '@folio/lib';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsControllerSetDocumentCategory, ForUserError } from 'korint-sdk';
import { ArrowRightLeftIcon, Loader2Icon, PenIcon, RotateCcwIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { DocumentCustomCategorieMapping, DocumentViewerDocument } from '../types';
import { useTranslation } from 'react-i18next';
import { parseAIClassificationMetadata } from '../DocumentClassificationStatus';
import { isUnresolvedCategory } from '@folio/utils/document-categories';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

function findNextUnclassifiedDocument(
  documents: DocumentViewerDocument[],
  currentDocumentId: string,
): DocumentViewerDocument | null {
  const currentIndex = documents.findIndex(d => d.id === currentDocumentId);

  for (let i = currentIndex + 1; i < documents.length; i++) {
    const doc = documents[i];
    if (
      (doc.type === 'supporting_document' || doc.type === 'non_delegated') &&
      isUnresolvedCategory(doc.category)
    ) {
      return doc;
    }
  }

  return null;
}

interface DocumentClassificationSelectorProps {
  needsReview?: boolean;
  document: DocumentViewerDocument;
  categories: DocumentCustomCategorieMapping[];
  documents: DocumentViewerDocument[];
  onDocumentChange: (doc: DocumentViewerDocument) => void;
  onClose: () => void;
}

export function DocumentClassificationSelector({
  needsReview = false,
  document,
  categories,
  documents,
  onDocumentChange,
  onClose,
}: DocumentClassificationSelectorProps) {
  const { t } = useTranslation();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const aiClassification = useMemo(() => {
    return parseAIClassificationMetadata(document.metadata.aiclassification);
  }, [document.metadata.aiclassification]);

  const originalAiCategory =
    aiClassification?.suggestedCategory ?? aiClassification?.suggestedcategory;
  const isManuallyOverridden = aiClassification?.status === 'manually_corrected';

  const currentCategory = useMemo(
    () => (document.category ? String(document.category) : undefined),
    [document.category],
  );

  const categoryOptions = useMemo(
    () =>
      categories.map(({ category }) => ({
        value: category,
        label: t(`documents.${category}`),
      })),
    [categories, t],
  );

  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    currentCategory ?? undefined,
  );
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    setSelectedCategory(currentCategory ?? undefined);
  }, [currentCategory, document.id]);

  const { mutate: updateDocument, isPending } = useMutation({
    mutationFn: (categoryValue: string) => {
      return documentsControllerSetDocumentCategory(document.id, {
        category: categoryValue,
        // Gate on the metadata itself, not traceId — the backend stopped
        // writing traceId with the Dash0 migration (feedback is keyed on the
        // metadata's aiOperationId server-side).
        ...(aiClassification && {
          aiCorrection: {
            originalCategory:
              aiClassification.suggestedCategory ?? aiClassification.suggestedcategory ?? '',
          },
        }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setPopoverOpen(false);

      if (needsReview || !isUnresolvedCategory(currentCategory)) return;

      const nextUnclassified = findNextUnclassifiedDocument(documents, document.id);

      if (nextUnclassified) {
        onDocumentChange(nextUnclassified);
      } else {
        onClose();
      }
    },
    onError: (error: ForUserError) => {
      setSelectedCategory(currentCategory);
      if (error.userError) return;
      toast({
        title: error.userError ?? t('documents.classification.errorTitle'),
        description: error.userHelp ?? t('documents.classification.errorDescription'),
      });
    },
  });

  const handleCategoryChange = (categoryValue: string) => {
    if (categoryValue) {
      setSelectedCategory(categoryValue);
      updateDocument(categoryValue);
    }
  };

  const handleRevertToAI = () => {
    if (originalAiCategory) {
      setSelectedCategory(originalAiCategory);
      updateDocument(originalAiCategory);
    }
  };

  return (
    <span className={cn('inline-flex items-center gap-1 flex-shrink-0', needsReview && 'w-full')}>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size={needsReview ? 'default' : 'icon'}
            className={cn(
              'h-9 rounded text-slate-500 hover:bg-slate-100',
              needsReview ? 'w-full text-amber-950 hover:bg-amber-100' : 'w-9',
            )}
            title={t('documents.classification.editCategory')}
            aria-label={t(
              needsReview ? 'documentReview.reviewCategory' : 'fileInput.classifyDocument',
            )}
            aria-busy={isPending}
            disabled={isPending}
            type="button"
          >
            {isPending ? (
              <Loader2Icon className="h-3 w-3 animate-spin" />
            ) : (
              <ArrowRightLeftIcon className="h-3 w-3" />
            )}
            {needsReview && <span className="ml-2">{t('documentReview.reviewCategory')}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-2">
          <SearchableSelect
            value={selectedCategory}
            options={categoryOptions}
            onChange={handleCategoryChange}
            disabled={isPending}
            placeholder={t('documents.classification.selectPlaceholder')}
            searchable
          />
          {needsReview &&
            aiClassification?.status === 'pending_review' &&
            currentCategory &&
            !isUnresolvedCategory(currentCategory) && (
              <Button
                className="mt-2 w-full"
                disabled={isPending}
                onClick={() => handleCategoryChange(currentCategory)}
              >
                {t('documentReview.confirmCategory')}
              </Button>
            )}
          {isManuallyOverridden && (
            <Group gap={2} className="mt-2 pt-2 border-t">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded cursor-help bg-blue-50 border border-blue-200"
                  >
                    <PenIcon className="w-2.5 h-2.5 text-blue-600" />
                    <Text inline size="xs" className="text-blue-600">
                      {t('documents.classification.corrected')}
                    </Text>
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <Text weight="medium" className="mb-1">
                    {t('documents.classification.manuallyCorrected')}
                  </Text>
                  {originalAiCategory && (
                    <Text size="xs" tone="muted">
                      IA : {t(`documents.${originalAiCategory}`, originalAiCategory)}
                    </Text>
                  )}
                  {aiClassification?.correction?.correctedAt && (
                    <Text size="xs" tone="muted" className="mt-1">
                      {t('documents.classification.correctedOn', {
                        date: formatDate(new Date(aiClassification.correction.correctedAt)),
                      })}
                    </Text>
                  )}
                  {aiClassification?.qualityFlags && aiClassification.qualityFlags.length > 0 && (
                    <div className="mt-1 pt-1 border-t border-border">
                      <Text size="xs" weight="medium" className="text-amber-600">
                        {t('documents.classification.qualityAlerts')}
                      </Text>
                      <ul className="text-xs text-muted-foreground">
                        {aiClassification.qualityFlags.map(flag => (
                          <li key={flag}>• {t(`aiClassification.qualityFlags.${flag}`, flag)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
              {originalAiCategory && (
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={handleRevertToAI}
                  disabled={isPending}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 h-5 px-1.5"
                >
                  <RotateCcwIcon className="h-2.5 w-2.5" />
                  <span>{t('documents.classification.revert')}</span>
                </Button>
              )}
            </Group>
          )}
        </PopoverContent>
      </Popover>
    </span>
  );
}
